import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  gastosAPI,
  tiposProveedoresAPI,
  proveedoresAPI,
  formasPagoAPI,
  bancosAPI,
  cuentasBancariasAPI,
  cajasAPI,
  withSaldoNegativoConfirm,
} from '../services/api';
import { toast } from 'react-toastify';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import FormasPagoEditor from '../components/FormasPagoEditor';
import { camposDolar } from '../utils/montoDolarFormaPago';
import '../Clientes.css';

const formatMoneda = (n) =>
  parseFloat(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const esChequeTipoPropio = (nombre) => {
  const n = (nombre || '').toLowerCase();
  return n.includes('cheque propio') || n.includes('cheque de tercero');
};

const GastoForm = () => {
  const navigate = useNavigate();
  const { fechaHoy } = useZonaHoraria();

  const [tiposProveedor, setTiposProveedor] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [formData, setFormData] = useState({ fecha: '', concepto: '', monto: '', tipo_proveedor_id: '', proveedor_id: '' });
  const [formasPagoGasto, setFormasPagoGasto] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, fecha: prev.fecha || fechaHoy() }));
  }, [fechaHoy]);

  const loadCatalogos = useCallback(async () => {
    try {
      const [resTipos, resFp, resBancos, resCb, resCajas] = await Promise.all([
        tiposProveedoresAPI.getAll(),
        formasPagoAPI.getDisponibles({ tipo_movimiento: 'egreso' }),
        bancosAPI.getAll({ activo: true }),
        cuentasBancariasAPI.getAll({ activo: true }),
        cajasAPI.getAll({ activo: true }),
      ]);
      if (resTipos.success) setTiposProveedor(resTipos.data || []);
      if (resFp.success) setFormasPago(resFp.data || []);
      if (resBancos.success) setBancos(resBancos.data || []);
      if (resCb.success) setCuentasBancarias(resCb.data || []);
      if (resCajas.success) setCajas(resCajas.data || []);
    } catch {
      toast.error('No se pudieron cargar los datos iniciales');
    }
  }, []);

  useEffect(() => {
    loadCatalogos();
  }, [loadCatalogos]);

  const loadProveedores = useCallback(async (tipoId) => {
    if (!tipoId) {
      setProveedores([]);
      return;
    }
    try {
      const res = await proveedoresAPI.getAll({ tipo_id: tipoId, activo: true });
      if (res.success) setProveedores(res.data || []);
    } catch {
      setProveedores([]);
    }
  }, []);

  useEffect(() => {
    loadProveedores(formData.tipo_proveedor_id);
  }, [formData.tipo_proveedor_id, loadProveedores]);

  const tipoProveedorPrevRef = React.useRef('');
  useEffect(() => {
    if (tipoProveedorPrevRef.current !== formData.tipo_proveedor_id) {
      tipoProveedorPrevRef.current = formData.tipo_proveedor_id;
      setFormData((prev) => ({ ...prev, proveedor_id: '' }));
    }
  }, [formData.tipo_proveedor_id]);

  const formasPagoHabilitadas = useMemo(
    () =>
      formasPago.filter((f) => {
        const tm = (f.tipo_movimiento || '').toLowerCase();
        return tm === 'egreso' || tm === 'ambos';
      }),
    [formasPago]
  );

  const construirFormasPagoPayload = () =>
    formasPagoGasto.map((fp) => {
      const formaPagoObj = formasPago.find((f) => String(f.id) === String(fp.forma_pago_id));
      const nombre = formaPagoObj?.nombre?.toLowerCase() || '';
      const resultado = {
        forma_pago_id: fp.forma_pago_id,
        forma_pago_nombre: formaPagoObj?.nombre,
        monto: parseFloat(fp.monto),
        ...camposDolar(fp, { formasPago, cuentasBancarias, cajas, modoEgreso: true }),
      };
      if (nombre.includes('cheque de tercero')) {
        if (fp.cheque_cartera_ids?.length) resultado.cheque_cartera_ids = fp.cheque_cartera_ids;
        else if (fp.cheque_cartera_id) resultado.cheque_cartera_id = fp.cheque_cartera_id;
      } else if (esChequeTipoPropio(nombre)) {
        resultado.cheque_propio = {
          cuenta_bancaria_id: fp.cheque_propio?.cuenta_bancaria_id || null,
          banco_id: null,
          numero: fp.cheque_propio?.numero,
          fecha_emision: fp.cheque_propio?.fecha_emision || formData.fecha,
          fecha_cobro: fp.cheque_propio?.fecha_cobro || null,
          importe: parseFloat(fp.monto),
          es_electronico: Boolean(fp.cheque_propio?.es_electronico),
        };
        if (resultado.cheque_propio.cuenta_bancaria_id) {
          resultado.cuenta_bancaria_id = Number(resultado.cheque_propio.cuenta_bancaria_id);
        }
      } else if (nombre.includes('cheque')) {
        resultado.cheque = {
          banco_id: fp.cheque?.banco_id,
          numero_cheque: fp.cheque?.numero_cheque,
          importe: parseFloat(fp.monto),
          fecha_emision: fp.cheque?.fecha_emision,
          fecha_cobro: fp.cheque?.fecha_cobro || null,
          titular: fp.cheque?.titular,
        };
      } else if (nombre.includes('transferencia')) {
        const tr = fp.transferencia || {};
        resultado.transferencia = {
          cuenta_bancaria_id: tr.cuenta_bancaria_id ?? formaPagoObj?.cuenta_bancaria_id ?? null,
          fecha: tr.fecha || formData.fecha,
          monto: parseFloat(tr.monto != null && tr.monto !== '' ? tr.monto : fp.monto),
        };
        if (resultado.transferencia.cuenta_bancaria_id) {
          resultado.cuenta_bancaria_id = Number(resultado.transferencia.cuenta_bancaria_id);
        }
      } else if (nombre.includes('tarjeta')) {
        resultado.tarjeta = { numero_ticket: fp.tarjeta?.numero_ticket };
        if (fp.caja_id) resultado.caja_id = Number(fp.caja_id);
      } else if (fp.caja_id) {
        // Efectivo / FP flexibles: caja elegida en UI
        resultado.caja_id = Number(fp.caja_id);
      }
      return resultado;
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const monto = parseFloat(formData.monto || 0);
    if (!formData.fecha) return toast.error('Ingrese la fecha');
    if (!formData.concepto.trim()) return toast.error('Ingrese el concepto');
    if (!formData.tipo_proveedor_id) return toast.error('Seleccione el tipo de proveedor');
    if (isNaN(monto) || monto <= 0) return toast.error('Ingrese un monto mayor a 0');
    if (formasPagoGasto.length === 0) return toast.error('Agregue al menos una forma de pago');
    const totalFp = formasPagoGasto.reduce((s, fp) => s + parseFloat(fp.monto || 0), 0);
    if (Math.abs(totalFp - monto) > 0.01) {
      return toast.error(`La suma de formas de pago (${formatMoneda(totalFp)}) debe ser igual al monto (${formatMoneda(monto)})`);
    }
    for (const fp of formasPagoGasto) {
      const nombre = (formasPago.find((f) => String(f.id) === String(fp.forma_pago_id))?.nombre || '').toLowerCase();
      if (nombre.includes('cheque de tercero')) {
        const ids = fp.cheque_cartera_ids?.length ? fp.cheque_cartera_ids : (fp.cheque_cartera_id ? [fp.cheque_cartera_id] : []);
        if (!ids.length) return toast.error('Seleccione cheques de la cartera');
      }
      if (nombre.includes('cheque propio')) {
        if (!fp.cheque_propio?.cuenta_bancaria_id || !fp.cheque_propio?.numero || !fp.cheque_propio?.fecha_emision) {
          return toast.error('Cheque propio: complete cuenta, número y fecha de emisión');
        }
      }
      if (nombre.includes('transferencia')) {
        const cuenta = fp.transferencia?.cuenta_bancaria_id
          || formasPago.find((f) => String(f.id) === String(fp.forma_pago_id))?.cuenta_bancaria_id;
        if (!cuenta) return toast.error('Transferencia: seleccione la cuenta bancaria');
      }
    }

    setLoading(true);
    try {
      const res = await withSaldoNegativoConfirm((confirmar) =>
        gastosAPI.crear({
          fecha: formData.fecha,
          concepto: formData.concepto.trim(),
          monto,
          tipo_proveedor_id: Number(formData.tipo_proveedor_id),
          proveedor_id: formData.proveedor_id ? Number(formData.proveedor_id) : null,
          formas_pago: construirFormasPagoPayload(),
          ...(confirmar ? { confirmar_saldo_negativo: true } : {}),
        })
      );
      if (res.success) {
        toast.success(res.message || 'Gasto registrado');
        navigate('/gastos');
      } else {
        toast.error(res.message || 'No se pudo registrar el gasto');
      }
    } catch (err) {
      if (!err.cancelled) toast.error(err.message || 'No se pudo registrar el gasto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="clientes-container">
      <div className="clientes-header">
        <h1>Nuevo gasto</h1>
      </div>

      <form onSubmit={handleSubmit} className="cliente-form" style={{ maxWidth: '900px' }}>
        <div className="form-row">
          <div className="form-group">
            <label>Fecha *</label>
            <input
              type="date"
              value={formData.fecha}
              onChange={(e) => setFormData((p) => ({ ...p, fecha: e.target.value }))}
              required
            />
          </div>
          <div className="form-group">
            <label>Monto *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.monto}
              onChange={(e) => setFormData((p) => ({ ...p, monto: e.target.value }))}
              required
              placeholder="0.00"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Concepto *</label>
          <input
            type="text"
            value={formData.concepto}
            onChange={(e) => setFormData((p) => ({ ...p, concepto: e.target.value }))}
            required
            placeholder="Descripción del gasto"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Tipo de proveedor *</label>
            <select
              value={formData.tipo_proveedor_id}
              onChange={(e) => setFormData((p) => ({ ...p, tipo_proveedor_id: e.target.value }))}
              required
            >
              <option value="">Seleccione...</option>
              {tiposProveedor.map((t) => (
                <option key={t.id} value={t.id}>{t.nombre}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Proveedor (opcional)</label>
            <select
              value={formData.proveedor_id}
              onChange={(e) => setFormData((p) => ({ ...p, proveedor_id: e.target.value }))}
              disabled={!formData.tipo_proveedor_id}
            >
              <option value="">Sin proveedor específico</option>
              {proveedores.map((p) => (
                <option key={p.id} value={p.id}>{p.razon_social}</option>
              ))}
            </select>
          </div>
        </div>

        <FormasPagoEditor
          value={formasPagoGasto}
          onChange={setFormasPagoGasto}
          formasPago={formasPagoHabilitadas}
          bancos={bancos}
          setBancos={setBancos}
          cuentasBancarias={cuentasBancarias}
          cajas={cajas}
          fecha={formData.fecha}
          fechaHoy={fechaHoy}
          montoTotal={formData.monto}
          seccionTitulo="Formas de pago del gasto *"
          modoEgreso
        />

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/gastos')}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : 'Registrar gasto'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GastoForm;
